import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import TeamComparison from "../components/Events/TeamComparison";
import GameStats from "../components/Events/GameStats";
import GameHighs from "../components/Events/GameHighs";
import PageContainer from "./PageContainer";
import "../assets/games.css";
import Loader from "../components/Utils/Loader";
import { toast } from "react-toastify";
import { colors, typography, borders, spacing } from "../assets/theme_setup";

const SingleGame = () => {
  const location = useLocation();
  const { id } = useParams();
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);

        // If no ID in params, default to our sample game
        const gameId = id || "top_30_black_vs_top_30_white";

        const response = await axios.get(`/api/events/game/${gameId}`, {
          withCredentials: true,
        });

        setGameData(response.data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch game data:", err);
        setError("Failed to load game data. Please try again.");
        toast.error("Failed to load game data");
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, [id]);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
          setActiveTab("stats");
        }, 100);
      }
    }
  }, [location, loading]);

  if (loading) {
    return (
      <PageContainer>
        <div className="game-loading">
          <Loader />
          <p>Loading game data...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="game-error">
          <h2>Error Loading Game</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </PageContainer>
    );
  }

  if (!gameData) return null;

  const { homeTeam, awayTeam, date, venue, attendance } =
    gameData.gameInfo || {};
  const homeScore = Math.round(gameData?.comparison?.home?.points || 0);
  const awayScore = Math.round(gameData?.comparison?.away?.points || 0);
  const homeWon = homeScore > awayScore;

  return (
    <PageContainer>
      <div className="game-page-container">
        <div className="game-header-card">
          <div className="game-teams-wrapper">
            <div className={`game-team-box ${homeWon ? "winner" : ""}`}>
              <span className="game-team-name">{homeTeam}</span>
              <span className="game-team-score">{homeScore}</span>
            </div>
            <div className="game-vs-container">
              <span className="game-vs-text">VS</span>
            </div>
            <div className={`game-team-box ${!homeWon ? "winner" : ""}`}>
              <span className="game-team-name">{awayTeam}</span>
              <span className="game-team-score">{awayScore}</span>
            </div>
          </div>
        </div>

        <div className="game-content-tabs">
          <button
            className={`game-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`game-tab ${activeTab === "highs" ? "active" : ""}`}
            onClick={() => setActiveTab("highs")}
          >
            Game Highs
          </button>
          <button
            className={`game-tab ${activeTab === "stats" ? "active" : ""}`}
            onClick={() => setActiveTab("stats")}
          >
            Detailed Stats
          </button>
        </div>

        <div className="game-content-body">
          {activeTab === "overview" && (
            <div className="game-section game-overview">
              <h2 className="game-section-title">Team Comparison</h2>
              <TeamComparison
                comparison={gameData?.comparison}
                loading={loading}
              />
            </div>
          )}

          {activeTab === "highs" && (
            <div className="game-section game-highs">
              <h2 className="game-section-title">Game Highs</h2>
              <GameHighs gameHighs={gameData?.gameHighs} loading={loading} />
            </div>
          )}

          {activeTab === "stats" && (
            <div className="game-section game-statistics" id="game-stats">
              <h2 className="game-section-title">Player Statistics</h2>
              <GameStats
                playerStats={gameData?.playerStats}
                loading={loading}
              />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default SingleGame;
